/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */
/**
 * Slice to handle CRUD operation for landing page.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import agent from "../../api/agent";
import {
  BulkStoreIngestionResponse,
  DdClientPerformanceUIResponse,
  DdGeneralClientUIResponse,
} from "../../models/ApiResponse";
import { DdClientPerformanceResponse } from "../../models/responseModels/DdClientPerformanceResponse";

// type required for initial state.
type InitialState = {
  isLoading: boolean;
  clientData: DdClientPerformanceResponse[];
  errorMessage: string | null;
};

// initialize initial state
const initialState: InitialState = {
  isLoading: false,
  clientData: [],
  errorMessage: null,
};

// argument type required for server call for data on landing page
export interface ClientDataArgs {
  startDate: string;
  endDate: string;
}

// get client performance data from server
export const getClientPerformanceData = createAsyncThunk<
  DdClientPerformanceUIResponse,
  ClientDataArgs
>(
  "dedicated-delivery/overview-of-clients-response",
  async (clientDataArgs: ClientDataArgs) => {
    const response = await agent.DedicatedDeliveryAgent.getClientDashboardData(
      clientDataArgs
    );
    return response;
  }
);

// get client information from server for general page
export const getGeneralClientData = createAsyncThunk<
  DdGeneralClientUIResponse,
  number
>("dedicated-delivery/general-client-response", async (clientId: number) => {
  const response = await agent.DedicatedDeliveryAgent.getClientData(clientId);
  return response;
});

// reducer to handle actions on landing page
const dedicatedDeliverySlice = createSlice({
  name: "dedicatedDeliveryDashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getClientPerformanceData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getClientPerformanceData.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.clientData = action.payload.data;
        } else {
          state.clientData = [];
        }
      })
      .addCase(getClientPerformanceData.rejected, (state, action) => {
        state.isLoading = false;
        state.clientData = [];
        state.errorMessage = action.error.message!;
      });
  },
});

export default dedicatedDeliverySlice.reducer;
